/**
 * DG CUSTOM: looping collection slider.
 *
 * Built on native overflow scrolling rather than a transform track, so swipe, momentum, keyboard
 * and screen-reader order all work without being re-implemented. The loop is the set repeated on
 * both sides; when the scroll position drifts into a repeat, it is silently moved back by one set
 * width. The content either side is identical, so the move is invisible.
 */
class DGCollectionSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.dotsEl = this.querySelector('[data-slider-dots]');
    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');

    if (!this.track) return;

    this.originals = Array.from(this.track.children);
    if (this.originals.length < 2) return;

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.build();

    this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    window.addEventListener('resize', this.debounce(this.remeasure.bind(this), 200));

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
  }

  /**
   * @param {Function} fn - Callback.
   * @param {number} wait - Delay in ms.
   * @returns {Function}
   */
  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  get behavior() {
    return this.motion.matches ? 'auto' : 'smooth';
  }

  /** Clones the set either side, builds the dots, and parks the view on the real set. */
  build() {
    // One spare set of runway beyond whatever the viewport shows, in both directions. With less
    // than that the scroll hits its own end before the wrap can fire and the slider dead-stops
    // after a single cycle — and a short set at six-per-view would never overflow at all.
    const setWidth = this.measureSet();
    const copies = Math.max(2, Math.ceil(this.track.clientWidth / Math.max(setWidth, 1)) + 1);

    for (let i = 0; i < copies; i += 1) {
      this.track.append(...this.cloneSet());
      this.track.prepend(...this.cloneSet().reverse());
    }

    this.buildDots();

    if (this.prevBtn) this.prevBtn.hidden = false;
    if (this.nextBtn) this.nextBtn.hidden = false;
    if (this.dotsEl) this.dotsEl.hidden = false;

    this.remeasure();
  }

  /**
   * Copies of the real slides, hidden from assistive tech so the same collections are not
   * announced several times over.
   * @returns {Array<Element>}
   */
  cloneSet() {
    return this.originals.map((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');

      clone.querySelectorAll('a').forEach((link) => { link.tabIndex = -1; });
      return clone;
    });
  }

  /** Width of one full set of slides, including the gap that follows each. */
  measureSet() {
    return this.originals.reduce((total, slide) => {
      const style = window.getComputedStyle(this.track);
      return total + slide.getBoundingClientRect().width + parseFloat(style.columnGap || 0);
    }, 0);
  }

  buildDots() {
    if (!this.dotsEl) return;

    this.dots = this.originals.map((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'DG-collection-slider__dot';

      // aria-current, not the tab role: these select a slide, they do not switch tabpanels, and
      // announcing them as tabs promises keyboard behaviour that is not there.
      if (index === 0) dot.setAttribute('aria-current', 'true');

      const title = slide.querySelector('.DG-collection-card__title');
      dot.setAttribute('aria-label', title ? title.textContent.trim() : String(index + 1));

      dot.addEventListener('click', () => this.goTo(index));
      this.dotsEl.append(dot);
      return dot;
    });
  }

  /** Recalculates geometry and re-parks on the real set. Runs on load and after a resize. */
  remeasure() {
    const slides = Array.from(this.track.children);
    const first = slides[0].getBoundingClientRect();
    const second = slides[1].getBoundingClientRect();

    this.span = second.left - first.left;
    this.setSpan = this.span * this.originals.length;

    // Where the real (non-clone) set begins.
    this.origin = this.originals[0].offsetLeft - this.track.offsetLeft;

    this.track.scrollLeft = this.origin;
    this.updateDots();
  }

  onScroll() {
    if (!this.frame) {
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.updateDots();
      });
    }

    // Only once movement has stopped. Re-seating mid-scroll would cancel a smooth animation and
    // swallow the rest of its travel, which reads as the slider refusing to advance.
    if (!this.settle) this.settle = this.debounce(this.wrap.bind(this), 150);
    this.settle();
  }

  /**
   * Re-seats the scroll position on the real set after it has run into a repeat. The content
   * either side is identical and the assignment is instant, so nothing is visible.
   */
  wrap() {
    if (!this.setSpan) return;

    const offset = this.track.scrollLeft - this.origin;
    const wrapped = ((offset % this.setSpan) + this.setSpan) % this.setSpan;

    if (Math.abs(wrapped - offset) > 1) {
      this.track.scrollLeft = this.origin + wrapped;
    }
  }

  /** @returns {number} Index within the real set that is currently at the left edge. */
  currentIndex() {
    if (!this.span) return 0;

    const offset = Math.round((this.track.scrollLeft - this.origin) / this.span);
    const count = this.originals.length;

    return ((offset % count) + count) % count;
  }

  updateDots() {
    if (!this.dots) return;

    const current = this.currentIndex();
    this.dots.forEach((dot, index) => {
      if (index === current) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** @param {number} direction - -1 for previous, 1 for next. */
  step(direction) {
    this.track.scrollBy({ left: this.span * direction, behavior: this.behavior });
  }

  /**
   * Scrolls to a slide by its index in the real set, taking the shorter way round.
   * @param {number} index - Index within the real set.
   */
  goTo(index) {
    const count = this.originals.length;
    let delta = index - this.currentIndex();

    if (delta > count / 2) delta -= count;
    if (delta < -count / 2) delta += count;

    this.track.scrollBy({ left: this.span * delta, behavior: this.behavior });
  }
}

if (!customElements.get('dg-collection-slider')) {
  customElements.define('dg-collection-slider', DGCollectionSlider);
}
